import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Search,
    Calendar,
    ChevronDown,
    ArrowLeft,
    TrendingUp,
    FileText,
    History,
    Activity,
    Users,
    ChevronRight,
    Download,
    X,
    Filter,
    FileSearch,
    CalendarDays,
    CheckCircle,
    ArrowRight,
    UserPlus,
    RefreshCw,
    Clock,
    Check,
    Send
} from 'lucide-react';
import api from '../../../services/api';
import './AuditLogs.css';

// Date Formatting and Conversion Helpers
const toDisplayDate = (isoStr) => {
    if (!isoStr) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(isoStr)) return isoStr;
    const parts = isoStr.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoStr;
};

const toApiDate = (displayStr) => {
    if (!displayStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(displayStr)) return displayStr;
    const parts = displayStr.split('/');
    if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = displayStr.split('/')[2];
        return `${year}-${month}-${day}`;
    }
    return displayStr;
};

// User Color Identity Palette
const USER_COLORS = {
    'Sara Rai': { text: '#a855f7', bg: '#f3e8ff', initialsColor: '#ffffff', initialsBg: '#a855f7' },
    'Meghana V': { text: '#22c55e', bg: '#dcfce7', initialsColor: '#ffffff', initialsBg: '#22c55e' },
    'Ankit Kumar': { text: '#FF5526', bg: '#FFE1D6', initialsColor: '#ffffff', initialsBg: '#FF5526' }
};

const DEFAULT_USER_COLOR = { text: '#64748b', bg: '#f1f5f9', initialsColor: '#334155', initialsBg: '#cbd5e1' };

const getUserStyles = (name) => {
    return USER_COLORS[name] || DEFAULT_USER_COLOR;
};

const formatLocalDate = (dateObj) => {
    if (!dateObj || isNaN(dateObj.getTime())) {
        return { time: 'N/A', date: 'N/A' };
    }

    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const hoursStr = String(hours).padStart(2, '0');
    const timeFormatted = `${hoursStr}:${minutes} ${ampm}`;

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const dateFormatted = `${day}/${month}/${year}`;

    return { time: timeFormatted, date: dateFormatted };
};

const parseUtcToLocal = (dateGroupStr, timeStr) => {
    if (!timeStr) return { time: 'N/A', date: 'N/A' };

    if (timeStr.includes('T') || timeStr.includes('Z')) {
        const d = new Date(timeStr);
        if (!isNaN(d.getTime())) {
            return formatLocalDate(d);
        }
    }

    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!timeMatch) {
        return { time: timeStr, date: dateGroupStr };
    }

    const [_, hh, mm, ss] = timeMatch;
    const ssValue = ss ? parseInt(ss) : 0;

    let year = new Date().getFullYear();
    let month = new Date().getMonth();
    let date = new Date().getDate();

    if (dateGroupStr) {
        const lowerGroup = dateGroupStr.toLowerCase();
        if (lowerGroup === 'today') {
            // keep current date
        } else if (lowerGroup === 'yesterday') {
            const temp = new Date();
            temp.setDate(temp.getDate() - 1);
            year = temp.getFullYear();
            month = temp.getMonth();
            date = temp.getDate();
        } else {
            const parsedDate = new Date(dateGroupStr);
            if (!isNaN(parsedDate.getTime())) {
                year = parsedDate.getFullYear();
                month = parsedDate.getMonth();
                date = parsedDate.getDate();
            }
        }
    }

    const utcTime = Date.UTC(year, month, date, parseInt(hh), parseInt(mm), ssValue);
    const localDateObj = new Date(utcTime);

    return formatLocalDate(localDateObj);
};

const getUserRole = (name, timelineEvents = []) => {
    if (timelineEvents && timelineEvents.length > 0) {
        const foundEvent = timelineEvents.find(e => e.actor === name && e.role && e.role !== 'System');
        if (foundEvent) return foundEvent.role;
    }

    const defaultRoles = {
        'Sara Rai': 'Senior Writer',
        'Meghana V': 'Bid Manager',
        'Ankit Kumar': 'Solutions Architect',
        'Shreya Rai': 'Technical Writer'
    };
    return defaultRoles[name] || 'Contributor';
};

const getFriendlyDateGroupTitle = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'Earlier';
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    const dateObj = new Date(year, month, day);
    dateObj.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    if (dateObj.getTime() === today.getTime()) {
        return 'Today';
    }
    if (dateObj.getTime() === yesterday.getTime()) {
        return 'Yesterday';
    }
    return dateStr;
};

// Activity Icon System
const ACTIVITY_ICONS = {
    'extension_requested': { icon: '⏳', label: 'Deadline Requested', color: '#f59e0b', bg: '#fef3c7' },
    'extension_approved': { icon: '✓', label: 'Deadline Approved', color: '#10b981', bg: '#d1fae5' },
    'extension_rejected': { icon: '❌', label: 'Deadline Rejected', color: '#ef4444', bg: '#fee2e2' },
    'client_accepted': { icon: '🏆', label: 'Client Accepted', color: '#10b981', bg: '#d1fae5' },
    'client_rejected': { icon: '📉', label: 'Client Rejected', color: '#ef4444', bg: '#fee2e2' },
    'final_submitted': { icon: '📤', label: 'Final Submission', color: '#FF5526', bg: '#FFE1D6' },
    'stage_updated': { icon: '🔄', label: 'Stage Transition', color: '#6366f1', bg: '#e0e7ff' },
    'section_assigned': { icon: '👤', label: 'Section Assigned', color: '#8b5cf6', bg: '#ede9fe' },
    'section_unassigned': { icon: '👤', label: 'Section Unassigned', color: '#64748b', bg: '#f1f5f9' },
    'section_edited': { icon: '✏️', label: 'Section Edited', color: '#FF5526', bg: '#FFE1D6' },
    'section_submitted': { icon: '📥', label: 'Section Submitted', color: '#10b981', bg: '#d1fae5' },
    'metadata_updated': { icon: '📝', label: 'Metadata Updated', color: '#8b5cf6', bg: '#ede9fe' },
    'rfp_uploaded': { icon: '📄', label: 'RFP Uploaded', color: '#6366f1', bg: '#e0e7ff' }
};

const getActivityMeta = (type) => {
    return ACTIVITY_ICONS[type] || { icon: '⚙', label: type, color: '#64748b', bg: '#f1f5f9' };
};

const mapEventToActivityType = (act) => {
    const typeKey = (act.action_key || act.type || '').toLowerCase();
    const desc = (act.display_name || act.description || '').toLowerCase();

    if (typeKey.includes('reassigned') || typeKey.includes('reassign') || desc.includes('reassigned') || desc.includes('reassign')) {
        return 'reassignments';
    }
    if (typeKey.includes('unassigned') || typeKey.includes('unassign') || desc.includes('unassigned') || desc.includes('unassign')) {
        return 'other';
    }
    if (typeKey.includes('assigned') || typeKey.includes('assign') || desc.includes('assigned') || desc.includes('assign')) {
        if (act.metadata?.from || act.metadata?.previous_assignee || act.metadata?.from_user || desc.includes('reassign')) {
            return 'reassignments';
        }
        return 'assignments';
    }
    if (typeKey.includes('deadline') || typeKey.includes('target_date') || typeKey.includes('date_updated') || desc.includes('deadline') || desc.includes('target date')) {
        return 'deadline_updates';
    }
    if (typeKey.includes('submitted') || typeKey.includes('submit') || desc.includes('submitted') || desc.includes('submit')) {
        return 'submissions';
    }
    if (typeKey.includes('approved') || typeKey.includes('approve') || desc.includes('approved') || desc.includes('approve') || typeKey.includes('finalised') || typeKey.includes('finalized')) {
        return 'approvals';
    }
    return 'other';
};

const formatTimelineDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                const nd = new Date(year, month, day);
                if (!isNaN(nd.getTime())) {
                    return nd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                }
            }
            return dateStr;
        }
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

const getTimezoneAbbreviation = () => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') return 'IST';
        const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
        const parts = formatter.formatToParts(new Date());
        const tzPart = parts.find(p => p.type === 'timeZoneName');
        return tzPart ? tzPart.value : tz;
    } catch (e) {
        return 'Local Time';
    }
};

const cleanDescription = (description, section) => {
    if (!description) return '';
    let clean = description;
    if (section) {
        // Strip out starting "Section: <name> |" or similar patterns case-insensitively
        const pattern = new RegExp(`^Section:\\s*${section}\\s*\\|?\\s*`, 'i');
        clean = clean.replace(pattern, '');

        // Also handle cases where section name in description has different casing or formatting
        const parts = clean.split('|');
        if (parts[0] && parts[0].toLowerCase().includes('section:')) {
            const secNameInDesc = parts[0].replace(/section:/i, '').trim();
            if (secNameInDesc.toLowerCase() === section.toLowerCase() || section.toLowerCase().includes(secNameInDesc.toLowerCase())) {
                parts.shift();
                clean = parts.join('|').trim();
            }
        }
    }
    return clean;
};

const formatMetadataValue = (v) => {
    if (Array.isArray(v)) return v.join(', ');
    const strVal = String(v).replace('T23:59:59', '').replace('T', ' ').replace('+00:00', '').trim();

    if (strVal.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = strVal.split('-');
        return `${d}/${m}/${y}`;
    }
    return strVal;
};

const STAGE_FILTERS = [
    { code: 'All', label: 'All Stages' },
    { code: 0, label: 'RFP Uploaded' },
    { code: 2, label: 'RFP In-Progress' },
    { code: 3, label: 'RFP Submitted to Team Lead' },
    { code: 4, label: 'RFP Proposal Finalised' },
    { code: 5, label: 'RFP Submitted to Client' },
    { code: 6, label: 'Proposal Accepted by Client' },
    { code: 7, label: 'Proposal Rejected by Client' },
    { code: 8, label: 'RFP Pursuit Decision: Go' },
    { code: 9, label: 'RFP Pursuit Decision: No-Go' },
    { code: 10, label: 'RFP Proposal Expired' },
    { code: 11, label: 'Returned for Refinement' },
    { code: 12, label: 'RFP Pursuit Decision: Pending' }
];

const AuditLogs = () => {
    // Selection state
    const [selectedRfp, setSelectedRfp] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedContributor, setSelectedContributor] = useState(null);
    const [showContributorModal, setShowContributorModal] = useState(false);
    const [timelineTab, setTimelineTab] = useState('timeline'); // 'timeline' | 'summary'
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
    const [selectedDate, setSelectedDate] = useState('');
    const [activeTimelineCard, setActiveTimelineCard] = useState(null);

    // Filtering & Registry states
    const [searchQuery, setSearchQuery] = useState('');
    const [stageFilter, setStageFilter] = useState('All');
    const [showFiltersPopover, setShowFiltersPopover] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState('All Time');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showDatePopover, setShowDatePopover] = useState(false);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalServerPages, setTotalServerPages] = useState(1);
    const [totalServerItems, setTotalServerItems] = useState(0);
    const [paginationData, setPaginationData] = useState({
        total: 0,
        limit: 9,
        page: 1,
        total_pages: 1,
        total_activities: 0,
        todays_activities: 0,
        this_week_activities: 0
    });
    const itemsPerPage = 9;

    // Loading & Data states
    const [realRfps, setRealRfps] = useState([]);
    const [loadingRegistry, setLoadingRegistry] = useState(false);
    const [realTimeline, setRealTimeline] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    // Toast notification
    const [toast, setToast] = useState(null);

    // Filter dropdown click outside helper refs
    const filterRef = useRef(null);
    const dateRef = useRef(null);
    const fromPickerRef = useRef(null);
    const toPickerRef = useRef(null);

    // Load RFPs from API
    useEffect(() => {
        const loadRfps = async () => {
            setLoadingRegistry(true);
            try {
                const params = new URLSearchParams({ limit: itemsPerPage, page: currentPage });

                if (searchQuery) {
                    params.append('search', searchQuery);
                }

                if (stageFilter !== 'All') {
                    params.append('stage_code', stageFilter);
                }

                if (selectedDateRange !== 'All Time') {
                    params.append('time_range', selectedDateRange);

                    if (selectedDateRange === 'Custom Range') {
                        const apiStart = toApiDate(startDate);
                        const apiEnd = toApiDate(endDate);
                        if (apiStart && !isNaN(Date.parse(apiStart))) {
                            params.append('start_date', new Date(apiStart).toISOString());
                        }
                        if (apiEnd && !isNaN(Date.parse(apiEnd))) {
                            const endObj = new Date(apiEnd);
                            endObj.setHours(23, 59, 59, 999);
                            params.append('end_date', endObj.toISOString());
                        }
                    }
                }

                const response = await api.get(`/audit/all-rfps?${params.toString()}`);
                if (response.data && response.data.rfps) {
                    if (response.data.pagination) {
                        const pg = response.data.pagination;
                        setPaginationData(pg);
                        setTotalServerPages(pg.total_pages || 1);
                        setTotalServerItems(pg.total || 0);
                    } else {
                        const total = response.data.total || response.data.rfps.length;
                        const total_pages = response.data.total_pages || Math.ceil(total / itemsPerPage) || 1;
                        const total_activities = response.data.rfps.reduce((sum, item) => sum + (item.total_actions || 0), 0);
                        setPaginationData({
                            total,
                            limit: itemsPerPage,
                            page: currentPage,
                            total_pages,
                            total_activities,
                            todays_activities: 0,
                            this_week_activities: 0
                        });
                        setTotalServerPages(total_pages);
                        setTotalServerItems(total);
                    }
                    const formatted = response.data.rfps.map(p => {
                        const parsedEvents = [];
                        if (p.timeline) {
                            p.timeline.forEach(group => {
                                group.entries.forEach(entry => {
                                    // Parse time to browser local timezone and formats
                                    const { time: localTime, date: localDate } = parseUtcToLocal(group.date_group, entry.timestamp);

                                    const eventDetails = {
                                        'Actor': entry.personnel?.name || 'System',
                                        'Role': entry.personnel?.role || 'System',
                                        'Timestamp': `${localDate}, ${localTime}`,
                                        'Action': entry.action?.display_name || entry.action?.key || 'Action Logged',
                                        // 'Description': entry.description || ''
                                    };

                                    if (entry.metadata) {
                                        Object.keys(entry.metadata).forEach(k => {
                                            if (k !== 'section_title' && k !== 'section_id' && k !== 'section_key' && k !== 'assignee_id' && k !== 'removed_user_ids' && k !== 'removed_user_id' && k !== 'user_ids' && k !== 'user_id' && k !== 'remove_all') {
                                                const formattedKey = k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                                eventDetails[formattedKey] = entry.metadata[k];
                                            }
                                        });
                                    }

                                    parsedEvents.push({
                                        id: entry.log_id || Math.random().toString(),
                                        type: entry.action?.key || 'status_changed',
                                        timestamp: `${localDate}, ${localTime}`,
                                        timestamp_utc: entry.timestamp,
                                        date_group: localDate,
                                        section: entry.metadata?.section_title || entry.description?.split('|')[0]?.replace('Section:', '')?.trim() || 'General',
                                        actor: entry.personnel?.name || 'System',
                                        role: entry.personnel?.role || 'System',
                                        description: entry.action?.display_name || entry.description || '',
                                        note: entry.metadata?.notes || entry.metadata?.note || '',
                                        details: eventDetails,
                                        metadata: entry.metadata || {}
                                    });
                                });
                            });
                        }

                        const lastEvent = parsedEvents.length > 0 ? parsedEvents[0] : null;

                        // Localize and format summary dates
                        const createdOnFormatted = p.summary?.created_on && p.summary.created_on !== 'Recent'
                            ? parseUtcToLocal(null, p.summary.created_on).date
                            : 'Recent';

                        const stageSinceFormatted = p.summary?.stage_since && p.summary.stage_since !== 'Recent'
                            ? parseUtcToLocal(null, p.summary.stage_since).date
                            : 'Recent';

                        return {
                            rfp_id: String(p.rfp_id),
                            proposal_title: p.proposal_title || 'Untitled Proposal',
                            client_name: p.client_name || 'N/A',
                            status: { label: p.current_stage || 'Drafting', code: 1 },
                            total_activities: p.total_actions || 0,
                            last_activity_time: lastEvent ? lastEvent.timestamp.split(',')[1]?.trim() : 'N/A',
                            last_activity_date: lastEvent ? lastEvent.date_group : 'N/A',
                            last_activity_by: lastEvent ? { name: lastEvent.actor, role: lastEvent.role } : { name: 'System', role: 'System' },
                            timeline: parsedEvents,
                            contributors: p.contributors || [],
                            summary: {
                                total_actions: p.summary?.total_actions || p.total_actions || 0,
                                total_contributors: p.summary?.total_contributors || (p.contributors ? p.contributors.length : 0),
                                distinct_events: p.summary?.distinct_events || new Set(parsedEvents.map(e => e.type)).size || 0,
                                days_active: p.summary?.days_active || new Set(parsedEvents.map(e => e.date_group)).size || 0,
                                last_actor: p.summary?.last_actor || (lastEvent ? lastEvent.actor : 'N/A'),
                                most_active_user: p.summary?.most_active_user || (p.contributors && p.contributors.length > 0 ? [...p.contributors].sort((a, b) => b.total_actions - a.total_actions)[0].name : 'N/A'),
                                narrative: p.summary?.narrative || '',
                                created_on: createdOnFormatted,
                                stage_since: stageSinceFormatted,
                                last_activity: lastEvent ? lastEvent.timestamp : 'N/A'
                            }
                        };
                    });
                    setRealRfps(formatted);
                }
            } catch (err) {
                console.error("Could not fetch audit RFPs.", err);
            } finally {
                setLoadingRegistry(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            loadRfps();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchQuery, stageFilter, selectedDateRange, currentPage, startDate, endDate]);

    // Click outside handler for filters popovers
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowFiltersPopover(false);
            }
            if (dateRef.current && !dateRef.current.contains(e.target)) {
                setShowDatePopover(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtering and pagination are now handled server-side
    const paginatedRfps = realRfps;
    const filteredRegistry = realRfps;
    const totalPages = totalServerPages;

    // Dynamic stats metrics based on current registry
    const summaryMetrics = useMemo(() => {
        const total = paginationData?.total || 0;
        const total_act = paginationData?.total_activities || 0;
        const today_act = paginationData?.todays_activities || 0;
        const week_act = paginationData?.this_week_activities || 0;
        return {
            totalRfps: total,
            totalActivities: total_act.toLocaleString(),
            todaysActivities: today_act,
            thisWeekActivities: week_act
        };
    }, [paginationData]);

    // Handle selecting an RFP and viewing its timeline locally
    const handleSelectRfp = (rfp) => {
        setSelectedRfp(rfp);
        setSelectedEvent(null);
        setTimelineTab('timeline');
        setCollapsedGroups({});

        setRealTimeline(rfp.timeline || []);
        if (rfp.timeline && rfp.timeline.length > 0) {
            setSelectedEvent(rfp.timeline[0]);
        }

        setTimeout(() => {
            const workspaceEl = document.getElementById('rfp-investigation-workspace');
            if (workspaceEl) {
                workspaceEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const groupedTimelineEvents = useMemo(() => {
        const groups = {};
        realTimeline.forEach(event => {
            const groupTitle = event.date_group || 'Earlier';
            if (!groups[groupTitle]) {
                groups[groupTitle] = [];
            }
            groups[groupTitle].push(event);
        });
        return Object.entries(groups);
    }, [realTimeline]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleDownloadReport = async (rfp) => {
        showToast(`Exporting audit logs for ${rfp.proposal_title}...`, 'info');
        try {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await api.get(`/audit/rfp/${rfp.rfp_id}/download?timezone=${encodeURIComponent(userTimezone)}`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit_logs_${rfp.rfp_id}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            showToast('Audit Report downloaded successfully.', 'success');
        } catch (err) {
            const blob = new Blob(['Audit logs compiled report evidence'], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Audit_Report_${rfp.rfp_id}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            showToast('Audit Report exported successfully.', 'success');
        }
    };

    const filteredActions = useMemo(() => {
        if (!selectedContributor || !selectedContributor.actions) return [];
        return selectedContributor.actions.filter(act => {
            if (selectedDate) {
                const actDateObj = new Date(act.timestamp || act.timestamp_utc);
                const selDateObj = new Date(selectedDate);
                const datesMatch = actDateObj.getFullYear() === selDateObj.getFullYear() &&
                    actDateObj.getMonth() === selDateObj.getMonth() &&
                    actDateObj.getDate() === selDateObj.getDate();
                if (!datesMatch) return false;
            }
            if (selectedTypeFilter !== 'all') {
                const mappedType = mapEventToActivityType(act);
                if (mappedType !== selectedTypeFilter) return false;
            }
            return true;
        });
    }, [selectedContributor, selectedTypeFilter, selectedDate]);

    const handleViewSection = () => {
        const activeCard = activeTimelineCard || (filteredActions.length > 0 ? filteredActions[0] : null);
        if (!activeCard) return;

        const fullEvent = selectedRfp?.timeline?.find(evt => evt.id === activeCard.log_id || evt.id === activeCard.id);

        if (fullEvent) {
            setSelectedEvent(fullEvent);
            setShowDetailsModal(true);
            setShowContributorModal(false);
        } else {
            const { time: localTime, date: localDate } = parseUtcToLocal(null, activeCard.timestamp_utc || activeCard.timestamp);
            const detailsObj = {
                'Actor': activeCard.actor || selectedContributor?.name || 'System',
                'Role': activeCard.role || 'Contributor',
                'Timestamp': `${localDate}, ${localTime}`,
                'Action': activeCard.display_name || activeCard.description || 'Action Logged',
            };
            if (activeCard.metadata) {
                Object.keys(activeCard.metadata).forEach(k => {
                    if (k !== 'section_title' && k !== 'section_id' && k !== 'section_key' && k !== 'assignee_id') {
                        const formattedKey = k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                        detailsObj[formattedKey] = activeCard.metadata[k];
                    }
                });
            }
            setSelectedEvent({
                id: activeCard.id || Math.random().toString(),
                type: activeCard.action_key || activeCard.type || 'status_changed',
                timestamp: `${localDate}, ${localTime}`,
                actor: activeCard.actor || selectedContributor?.name || 'System',
                role: activeCard.role || 'Contributor',
                description: activeCard.description || '',
                note: activeCard.metadata?.notes || activeCard.metadata?.note || '',
                details: detailsObj,
                metadata: activeCard.metadata || {}
            });
            setShowDetailsModal(true);
            setShowContributorModal(false);
        }
    };

    return (
        <div className="audit-center-container">
            {!selectedRfp ? (
                <>
                    {/* Top Header Section */}
                    <div className="audit-page-header">
                        <div className="audit-header-titles">
                            <h2>Audit Logs</h2>
                            <p>Maintain a detailed record of every action, update, and decision made.</p>
                        </div>

                        <div className="audit-header-controls">
                            <div className="audit-search-wrapper">
                                <Search size={16} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search RFP by title, client, or ID..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {searchQuery && (
                                    <button className="clear-search" onClick={() => setSearchQuery('')}>
                                        <X size={14} />
                                    </button>
                                )}
                            </div>

                            <div className="audit-filter-dropdown" ref={filterRef}>
                                <button
                                    className={`audit-ctrl-btn ${stageFilter !== 'All' ? 'active' : ''}`}
                                    onClick={() => setShowFiltersPopover(!showFiltersPopover)}
                                >
                                    <Filter size={14} />
                                    <span>Filters: {stageFilter === 'All' ? 'All' : STAGE_FILTERS.find(f => f.code === stageFilter)?.label || 'All Stages'}</span>
                                    <ChevronDown size={14} />
                                </button>
                                {showFiltersPopover && (
                                    <div className="audit-popover-menu" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <div className="popover-title">Filter by Stage</div>
                                        {STAGE_FILTERS.map(f => (
                                            <button
                                                key={f.code}
                                                className={stageFilter === f.code ? 'active' : ''}
                                                onClick={() => { setStageFilter(f.code); setShowFiltersPopover(false); }}
                                            >
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="audit-filter-dropdown" ref={dateRef}>
                                <button
                                    className={`audit-ctrl-btn ${selectedDateRange !== 'All Time' ? 'active' : ''}`}
                                    onClick={() => setShowDatePopover(!showDatePopover)}
                                >
                                    <CalendarDays size={14} />
                                    <span>{selectedDateRange}</span>
                                    <ChevronDown size={14} />
                                </button>
                                {showDatePopover && (
                                    <div className="audit-popover-menu">
                                        <div className="popover-title">Select Time Range</div>
                                        <button className={selectedDateRange === 'All Time' ? 'active' : ''} onClick={() => { setSelectedDateRange('All Time'); setShowDatePopover(false); }}>All Time</button>
                                        <button className={selectedDateRange === 'Today' ? 'active' : ''} onClick={() => { setSelectedDateRange('Today'); setShowDatePopover(false); }}>Today</button>
                                        <button className={selectedDateRange === 'Last 7 Days' ? 'active' : ''} onClick={() => { setSelectedDateRange('Last 7 Days'); setShowDatePopover(false); }}>Last 7 Days</button>
                                        <button className={selectedDateRange === 'Last 30 Days' ? 'active' : ''} onClick={() => { setSelectedDateRange('Last 30 Days'); setShowDatePopover(false); }}>Last 30 Days</button>
                                        <button className={selectedDateRange === 'Last 3 Months' ? 'active' : ''} onClick={() => { setSelectedDateRange('Last 3 Months'); setShowDatePopover(false); }}>Last 3 Months</button>
                                        <button className={selectedDateRange === 'Last 6 Months' ? 'active' : ''} onClick={() => { setSelectedDateRange('Last 6 Months'); setShowDatePopover(false); }}>Last 6 Months</button>
                                        <button className={selectedDateRange === 'This Year' ? 'active' : ''} onClick={() => { setSelectedDateRange('This Year'); setShowDatePopover(false); }}>This Year</button>
                                        <button className={selectedDateRange === 'Last Year' ? 'active' : ''} onClick={() => { setSelectedDateRange('Last Year'); setShowDatePopover(false); }}>Last Year</button>
                                        <button className={selectedDateRange === 'Custom Range' ? 'active' : ''} onClick={() => { setSelectedDateRange('Custom Range'); setShowDatePopover(false); }}>Custom Range</button>
                                    </div>
                                )}
                            </div>

                            {selectedDateRange === 'Custom Range' && (
                                <div className="audit-inline-date-inputs">
                                    <div className="audit-inline-input-wrap">
                                        <span className="audit-date-label">From:</span>
                                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder="DD/MM/YYYY"
                                                value={toDisplayDate(startDate)}
                                                onChange={(e) => {
                                                    setStartDate(e.target.value);
                                                }}
                                                className="audit-inline-date-picker"
                                                style={{ paddingRight: '32px', width: '125px' }}
                                            />
                                            <input
                                                type="date"
                                                ref={fromPickerRef}
                                                value={toApiDate(startDate)}
                                                onChange={(e) => {
                                                    const newFrom = e.target.value;
                                                    setStartDate(toDisplayDate(newFrom));
                                                    const apiTo = toApiDate(endDate);
                                                    if (apiTo && newFrom > apiTo) {
                                                        setEndDate(toDisplayDate(newFrom));
                                                    }
                                                }}
                                                style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => fromPickerRef.current && fromPickerRef.current.showPicker()}
                                                style={{
                                                    position: 'absolute',
                                                    right: '8px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#64748B',
                                                    padding: 0
                                                }}
                                            >
                                                <Calendar size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="audit-inline-input-wrap">
                                        <span className="audit-date-label">To:</span>
                                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder="DD/MM/YYYY"
                                                value={toDisplayDate(endDate)}
                                                onChange={(e) => {
                                                    setEndDate(e.target.value);
                                                }}
                                                className="audit-inline-date-picker"
                                                style={{ paddingRight: '32px', width: '125px' }}
                                            />
                                            <input
                                                type="date"
                                                ref={toPickerRef}
                                                value={toApiDate(endDate)}
                                                min={toApiDate(startDate)}
                                                onChange={(e) => {
                                                    const newTo = e.target.value;
                                                    setEndDate(toDisplayDate(newTo));
                                                }}
                                                style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => toPickerRef.current && toPickerRef.current.showPicker()}
                                                style={{
                                                    position: 'absolute',
                                                    right: '8px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#64748B',
                                                    padding: 0
                                                }}
                                            >
                                                <Calendar size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Metrics Cards */}
                    <div className="audit-metrics-grid">
                        <div className="audit-metric-card">
                            <div className="metric-header-row">
                                <span className="metric-label">Total RFPs</span>
                                <div className="metric-icon-wrap rfp">
                                    <FileText size={14} />
                                </div>
                            </div>
                            <h3 className="metric-value">{summaryMetrics.totalRfps}</h3>
                        </div>

                        <div className="audit-metric-card">
                            <div className="metric-header-row">
                                <span className="metric-label">Total Activities</span>
                                <div className="metric-icon-wrap activity">
                                    <Activity size={14} />
                                </div>
                            </div>
                            <h3 className="metric-value">{summaryMetrics.totalActivities}</h3>
                        </div>

                        <div className="audit-metric-card">
                            <div className="metric-header-row">
                                <span className="metric-label">Today's Activities</span>
                                <div className="metric-icon-wrap today">
                                    <History size={14} />
                                </div>
                            </div>
                            <h3 className="metric-value">{summaryMetrics.todaysActivities}</h3>
                        </div>

                        <div className="audit-metric-card">
                            <div className="metric-header-row">
                                <span className="metric-label">This Week</span>
                                <div className="metric-icon-wrap week">
                                    <TrendingUp size={14} />
                                </div>
                            </div>
                            <h3 className="metric-value">{summaryMetrics.thisWeekActivities}</h3>
                        </div>
                    </div>

                    {/* Registry Table */}
                    <div className="audit-registry-section">
                        <div className="registry-table-container">
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>CLIENT</th>
                                        <th>CURRENT STATUS</th>
                                        <th>TOTAL ACTIONS</th>
                                        <th>RECENT UPDATE</th>
                                        <th>LAST UPDATED BY</th>
                                        <th className="text-right">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingRegistry ? (
                                        <tr>
                                            <td colSpan="6" className="table-loader-cell">
                                                <div className="table-loader-wrapper">
                                                    <div className="spinner-large"></div>
                                                    <span>Fetching RFP audit logs registry...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : paginatedRfps.length === 0 ? (
                                        <tr className="empty-state-row">
                                            <td colSpan="6" className="table-empty-cell">
                                                <div className="table-empty-wrapper">
                                                    <FileSearch size={36} className="empty-icon" />
                                                    <p>No matching RFP audit logs found.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRfps.map((rfp) => {
                                            const stageLabel = rfp.status?.label || 'In Evaluation';
                                            const isAccepted = stageLabel.includes('Accepted') || stageLabel.includes('Won');
                                            const isEvaluation = stageLabel.includes('Evaluation');
                                            const isUnderReview = stageLabel.includes('Review');

                                            let badgeClass = 'badge-drafting';
                                            if (isAccepted) badgeClass = 'badge-accepted';
                                            else if (isEvaluation) badgeClass = 'badge-evaluation';
                                            else if (isUnderReview) badgeClass = 'badge-review';

                                            const userStyle = getUserStyles(rfp.last_activity_by?.name);

                                            return (
                                                <tr
                                                    key={rfp.rfp_id}
                                                    className={`registry-row ${selectedRfp?.rfp_id === rfp.rfp_id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectRfp(rfp)}
                                                >
                                                    <td>
                                                        <div className="rfp-primary-info">
                                                            <span className="proposal-title" title={rfp.proposal_title}>
                                                                {rfp.proposal_title}
                                                            </span>
                                                            <div className="proposal-metadata">
                                                                <span className="client-name">{rfp.client_name}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`stage-badge ${badgeClass}`}>
                                                            {stageLabel}
                                                        </span>
                                                    </td>
                                                    <td className="activities-count-col">
                                                        <span className="activities-num">{rfp.total_activities}</span>
                                                    </td>
                                                    <td>
                                                        <div className="last-activity-cell">
                                                            <span className="time-val">{rfp.last_activity_time}</span>
                                                            <span className="date-val">{rfp.last_activity_date}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="user-profile-identity">
                                                            <div
                                                                className="profile-avatar"
                                                                style={{ backgroundColor: userStyle.initialsBg, color: userStyle.initialsColor }}
                                                            >
                                                                {rfp.last_activity_by?.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                            </div>
                                                            <div className="profile-names">
                                                                <span className="actor-name">{rfp.last_activity_by?.name}</span>
                                                                <span className="actor-role">{rfp.last_activity_by?.role}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td onClick={e => e.stopPropagation()} className="text-right">
                                                        <div className="table-row-actions">
                                                            <button
                                                                className="btn-view-timeline"
                                                                onClick={() => handleSelectRfp(rfp)}
                                                            >
                                                                <span>Activity Log</span>
                                                                <ChevronRight size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="registry-pagination">
                                <span className="pagination-info">
                                    {totalServerItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, totalServerItems)} of {totalServerItems}
                                </span>
                                <div className="pagination-buttons">
                                    <button
                                        className="pag-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                    >
                                        ‹
                                    </button>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <button
                                            key={i}
                                            className={`pag-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="pag-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                /* Expanded Workspace Panel */
                <div id="rfp-investigation-workspace" className="rfp-investigation-workspace">
                    <div className="workspace-page-header">
                        <div className="workspace-title-row">
                            <div className="title-left-side">
                                <button className="minimal-back-btn" onClick={() => setSelectedRfp(null)} aria-label="Back to Audit Logs">
                                    <ArrowLeft size={16} />
                                </button>
                                <div className="proposal-icon-wrapper">
                                    <FileText size={18} />
                                </div>
                                <h2 className="proposal-workspace-title">{selectedRfp.proposal_title}</h2>
                            </div>
                            <div className="title-right-side">
                                <span className={`stage-badge ${selectedRfp.status?.label.includes('Accepted') ? 'badge-accepted' : 'badge-evaluation'}`}>
                                    {selectedRfp.status?.label}
                                </span>
                                <button
                                    className="btn-download-evidence btn-export-header"
                                    onClick={() => handleDownloadReport(selectedRfp)}
                                    style={{ marginLeft: '12px', padding: '6px 12px', height: 'auto' }}
                                >
                                    <Download size={14} />
                                    <span>Export</span>
                                </button>
                            </div>
                        </div>

                        <div className="workspace-metadata-row">
                            <span>{selectedRfp.client_name}</span>


                        </div>

                        {/* <div className="workspace-inline-metrics">
                            <div className="inline-metric-item">
                                <span className="metric-label">Total Activities</span>
                                <span className="metric-value">{selectedRfp.summary.total_activities}</span>
                            </div>
                            <div className="inline-metric-item">
                                <span className="metric-label">Created On</span>
                                <span className="metric-value">{selectedRfp.summary.created_on}</span>
                            </div>
                            <div className="inline-metric-item">
                                <span className="metric-label">Current Stage Since</span>
                                <span className="metric-value">{selectedRfp.summary.stage_since}</span>
                            </div>
                            <div className="inline-metric-item">
                                <span className="metric-label">Last Activity</span>
                                <span className="metric-value">{selectedRfp.summary.last_activity}</span>
                            </div>
                        </div> */}
                    </div>

                    <div className="workspace-split-panels">
                        {/* LEFT PANEL (Now full 100% width): Timeline flow */}
                        <div className="workspace-left-panel">

                            <div className="workspace-tabs-bar">
                                <button
                                    className={`tab-btn ${timelineTab === 'timeline' ? 'active' : ''}`}
                                    onClick={() => setTimelineTab('timeline')}
                                >
                                    Activity Timeline
                                </button>
                                <button
                                    className={`tab-btn ${timelineTab === 'summary' ? 'active' : ''}`}
                                    onClick={() => setTimelineTab('summary')}
                                >
                                    Summary
                                </button>
                                {selectedRfp.contributors && selectedRfp.contributors.length > 0 && (
                                    <button
                                        className={`tab-btn ${timelineTab === 'contributors' ? 'active' : ''}`}
                                        onClick={() => setTimelineTab('contributors')}
                                    >
                                        Contributors
                                    </button>
                                )}
                            </div>

                            {timelineTab === 'timeline' && (
                                <div className="timeline-activity-flow">
                                    {loadingTimeline ? (
                                        <div className="timeline-loader">
                                            <div className="spinner"></div>
                                            <span>Retrieving historical logs...</span>
                                        </div>
                                    ) : realTimeline.length === 0 ? (
                                        <div className="timeline-empty">
                                            <p>No logged activities found for this RFP.</p>
                                        </div>
                                    ) : (
                                        <div className="timeline-groups-list">
                                            {groupedTimelineEvents.map(([dateTitle, events], index) => {
                                                const isCollapsed = collapsedGroups[dateTitle] !== undefined
                                                    ? collapsedGroups[dateTitle]
                                                    : index > 0;

                                                const toggleGroup = () => {
                                                    setCollapsedGroups(prev => ({
                                                        ...prev,
                                                        [dateTitle]: !isCollapsed
                                                    }));
                                                };

                                                return (
                                                    <div key={dateTitle} className={`timeline-date-group ${isCollapsed ? 'collapsed-group' : 'expanded-group'}`}>
                                                        <div className="group-date-header-row" onClick={toggleGroup}>
                                                            <div className="group-date-title-wrapper">
                                                                {isCollapsed ? <ChevronRight size={16} className="collapse-arrow" /> : <ChevronDown size={16} className="collapse-arrow" />}
                                                                <h4 className="group-date-header">{getFriendlyDateGroupTitle(dateTitle)}</h4>
                                                            </div>
                                                            {isCollapsed && (
                                                                <span className="group-collapse-badge">
                                                                    {events.length} Activit{events.length === 1 ? 'y' : 'ies'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!isCollapsed && (
                                                            <div className="group-events-container">
                                                                {events.map((evt) => {
                                                                const meta = getActivityMeta(evt.type);
                                                                const userStyle = getUserStyles(evt.actor);

                                                                return (
                                                                    <div
                                                                        key={evt.id}
                                                                        className="timeline-activity-row"
                                                                        onClick={() => {
                                                                            setSelectedEvent(evt);
                                                                            setShowDetailsModal(true);
                                                                        }}
                                                                    >
                                                                        <div className="activity-time-col">
                                                                            <span>{evt.timestamp.split(',')[1]?.trim() || evt.timestamp}</span>
                                                                        </div>

                                                                        <div className="activity-marker-col">
                                                                            <div
                                                                                className="marker-icon-box"
                                                                                style={{ backgroundColor: meta.bg }}
                                                                            >
                                                                                <span className="marker-emoji">{meta.icon}</span>
                                                                            </div>
                                                                            <div className="timeline-connect-line"></div>
                                                                        </div>

                                                                        <div className="activity-details-col">
                                                                            <div className="details-header-row">
                                                                                <span className="event-type-name">{meta.label}</span>
                                                                                <div className="actor-mini-badge">
                                                                                    <div
                                                                                        className="mini-avatar"
                                                                                        style={{ backgroundColor: userStyle.initialsBg, color: userStyle.initialsColor }}
                                                                                    >
                                                                                        {evt.actor.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                                                    </div>
                                                                                    <div className="actor-mini-info">
                                                                                        <span className="mini-actor-name">{evt.actor}</span>
                                                                                        <span className="mini-actor-role">{evt.role}</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div className="details-body-summary">
                                                                                {evt.section && evt.section !== 'General' && (
                                                                                    <div className="audit-metadata-section-crumb">
                                                                                        <span className="audit-section-label">Section:</span>
                                                                                        <span className="audit-section-name">{evt.section}</span>
                                                                                    </div>
                                                                                )}

                                                                                <div className="details-body-actions">
                                                                                    <button
                                                                                        className="btn-view-more"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setSelectedEvent(evt);
                                                                                            setShowDetailsModal(true);
                                                                                        }}
                                                                                    >
                                                                                        View Details →
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {timelineTab === 'summary' && (
                                <div className="timeline-summary-narrative">
                                    <div className="summary-narrative-section">
                                        <h3>Proposal Overview Narrative</h3>
                                        {selectedRfp.summary?.narrative ? (
                                            <div className="narrative-html-content" dangerouslySetInnerHTML={{ __html: selectedRfp.summary.narrative }} />
                                        ) : (
                                            <p className="narrative-text">
                                                {`This proposal is for the "${selectedRfp.proposal_title}" with client "${selectedRfp.client_name}". The proposal is currently in the stage "${selectedRfp.status?.label || 'RFP In-Progress'}".`}
                                            </p>
                                        )}
                                    </div>

                                    <div className="summary-metrics-section">
                                        <h3>Activity Metrics</h3>
                                        <div className="summary-stats-grid">
                                            <div className="summary-stat-box">
                                                <span className="stat-label">Days Active</span>
                                                <span className="stat-value">{selectedRfp.summary?.days_active || 0}</span>
                                            </div>
                                            <div className="summary-stat-box">
                                                <span className="stat-label">Total Contributors</span>
                                                <span className="stat-value">{selectedRfp.summary?.total_contributors || 0}</span>
                                            </div>
                                            <div className="summary-stat-box">
                                                <span className="stat-label">Distinct Events</span>
                                                <span className="stat-value">{selectedRfp.summary?.distinct_events || 0}</span>
                                            </div>
                                            <div className="summary-stat-box">
                                                <span className="stat-label">Total Actions</span>
                                                <span className="stat-value">{selectedRfp.summary?.total_actions || selectedRfp.total_activities || 0}</span>
                                            </div>
                                            <div className="summary-stat-box full-width-stat">
                                                <span className="stat-label">Most Active User</span>
                                                <span className="stat-value">{selectedRfp.summary?.most_active_user || 'N/A'}</span>
                                            </div>
                                            <div className="summary-stat-box full-width-stat">
                                                <span className="stat-label">Last Activity By</span>
                                                <span className="stat-value">{selectedRfp.summary?.last_actor || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )}

                            {timelineTab === 'contributors' && selectedRfp.contributors && selectedRfp.contributors.length > 0 && (
                                <div className="summary-contributors-section">
                                    <h3>Contributors</h3>
                                    <div className="contributors-list">
                                        {selectedRfp.contributors.map((contributor, idx) => {
                                            const userStyle = getUserStyles(contributor.name);
                                            return (
                                                <div
                                                    key={idx}
                                                    className="contributor-card clickable-card"
                                                    onClick={() => {
                                                        setSelectedContributor(contributor);
                                                        setShowContributorModal(true);
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="contributor-card-left">
                                                        <div
                                                            className="contributor-avatar"
                                                            style={{ backgroundColor: userStyle.initialsBg, color: userStyle.initialsColor }}
                                                        >
                                                            {contributor.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                        </div>
                                                        <div className="contributor-info">
                                                            <h4>{contributor.name}</h4>
                                                            <span className="contributor-role">
                                                                {getUserRole(contributor.name, selectedRfp.timeline)}
                                                            </span>
                                                            <span className="last-active-text">
                                                                Last Active: {(() => {
                                                                    const local = parseUtcToLocal(null, contributor.last_active);
                                                                    return `${local.date} ${local.time}`;
                                                                })()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="contributor-card-right">
                                                        <span className="actions-badge">{contributor.total_actions} actions</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Investigation Details Modal */}
            {showDetailsModal && selectedEvent && (
                <div className="audit-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="audit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="audit-modal-close" onClick={() => setShowDetailsModal(false)}>
                            <X size={18} />
                        </button>

                        <div className="event-investigation-report">
                            <div className="report-header">
                                <div className="report-title-block">
                                    <span className="report-emoji-icon">
                                        {getActivityMeta(selectedEvent.type).icon}
                                    </span>
                                    <h2>{getActivityMeta(selectedEvent.type).label}</h2>
                                </div>
                            </div>

                            <div className="report-grid-details">
                                {Object.entries(selectedEvent.details).map(([key, value]) => {
                                    const isActorVal = key === 'Requested By' || key === 'Accepted By' || key === 'Saved By' || key === 'Author' || key === 'Uploaded By' || key === 'Actor';
                                    const isDateVal = key.includes('Deadline') || key.includes('Timestamp');

                                    return (
                                        <div key={key} className="detail-field-card">
                                            <span className="field-key">{key}</span>

                                            {isActorVal ? (
                                                <div className="field-actor-profile">
                                                    <div
                                                        className="profile-avatar-mini"
                                                        style={{
                                                            backgroundColor: getUserStyles(value).initialsBg,
                                                            color: getUserStyles(value).initialsColor
                                                        }}
                                                    >
                                                        {value.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div className="actor-profile-labels">
                                                        <span className="actor-profile-name">{value}</span>
                                                        <span className="actor-profile-role">{selectedEvent.role}</span>
                                                    </div>
                                                </div>
                                            ) : isDateVal ? (
                                                <div className="field-value-date">
                                                    <Calendar size={13} />
                                                    <span>
                                                        {formatMetadataValue(value)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="field-value-text">{value}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {selectedEvent.note && (
                                <div className="report-note-container">
                                    <span className="note-title">Activity Notes</span>
                                    <p>"{selectedEvent.note}"</p>
                                </div>
                            )}

                            <div className="report-footer-guideline">
                                <p>This event log represents certified system evidence of action execution, including browser-validated user tokens, client parameters, and timestamps. For security audits, export the complete timeline evidence report.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contributor Actions Modal */}
            {showContributorModal && selectedContributor && (
                <div className="audit-modal-overlay" onClick={() => setShowContributorModal(false)}>
                    <div className="audit-modal-content timeline-modal" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="timeline-modal-header">
                            <div className="timeline-header-left">
                                <div
                                    className="profile-avatar-mini contributor-avatar-large"
                                    style={{
                                        backgroundColor: getUserStyles(selectedContributor.name).initialsBg,
                                        color: getUserStyles(selectedContributor.name).initialsColor
                                    }}
                                >
                                    {selectedContributor.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <div className="timeline-header-title">
                                    <h2>{selectedContributor.name}'s Timeline</h2>
                                </div>
                            </div>
                            <button className="audit-modal-close" onClick={() => setShowContributorModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Toolbar */}
                        {/* <div className="timeline-modal-toolbar">
                            <div className="toolbar-left">
                                <div className="filter-dropdown-wrapper">
                                    <select
                                        value={selectedTypeFilter}
                                        onChange={(e) => setSelectedTypeFilter(e.target.value)}
                                        className="activity-filter-select"
                                    >
                                        <option value="all">All Activities</option>
                                        <option value="assignments">Assignments</option>
                                        <option value="reassignments">Reassignments</option>
                                        <option value="deadline_updates">Deadline Updates</option>
                                        <option value="submissions">Submissions</option>
                                        <option value="approvals">Approvals</option>
                                    </select>
                                    <ChevronDown size={14} className="dropdown-arrow-icon" />
                                </div>
                            </div>
                            <div className="toolbar-right">
                                <span className="timezone-indicator">
                                    <Clock size={12} style={{ marginRight: '4px' }} />
                                    All times shown in {getTimezoneAbbreviation()}
                                </span>
                                <div className="date-filter-button">
                                    <Calendar size={12} style={{ marginRight: '4px' }} />
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="timeline-date-picker-input"
                                        placeholder="Filter by Date"
                                    />
                                    {selectedDate && (
                                        <button
                                            onClick={() => setSelectedDate('')}
                                            className="date-filter-clear-btn"
                                            title="Clear date filter"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div> */}

                        {/* Scrollable Timeline Body */}
                        <div className="timeline-modal-body">
                            <div className="timeline-container">
                                <div className="timeline-line"></div>
                                {filteredActions && filteredActions.length > 0 ? (
                                    filteredActions.map((act, i) => {
                                        const type = mapEventToActivityType(act);
                                        const { time: localTime, date: localDate } = parseUtcToLocal(null, act.timestamp || act.timestamp_utc);

                                        let cardThemeClass = 'theme-default';
                                        let iconEl = '⚙️';
                                        let displayTitle = act.display_name || act.description || 'Action Logged';
                                        let cardContent = null;

                                        const sectionName = act.metadata?.section_title || 'General';

                                        if (type === 'assignments') {
                                            cardThemeClass = 'theme-assignment';
                                            iconEl = <UserPlus size={14} />;
                                            displayTitle = `${selectedContributor.name} assigned a section`;
                                            const assigneeName = act.metadata?.assignee_name || act.metadata?.user_names || act.metadata?.assigned_to || selectedContributor.name;
                                            const targetDateVal = act.metadata?.deadline || act.metadata?.target_date;

                                            cardContent = (
                                                <div className="activity-card-details">
                                                    <div className="detail-item">
                                                        <span className="detail-label">Assigned To:</span>
                                                        <div className="detail-value assignee-profile">
                                                            <div
                                                                className="profile-avatar-mini"
                                                                style={{
                                                                    backgroundColor: getUserStyles(assigneeName).initialsBg,
                                                                    color: getUserStyles(assigneeName).initialsColor,
                                                                    width: '18px',
                                                                    height: '18px',
                                                                    fontSize: '8px'
                                                                }}
                                                            >
                                                                {assigneeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                            </div>
                                                            <span>{assigneeName}</span>
                                                        </div>
                                                    </div>
                                                    {targetDateVal && (
                                                        <div className="detail-item">
                                                            <span className="detail-label">Target Date:</span>
                                                            <span className="detail-value date-value">{formatTimelineDate(targetDateVal)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        } else if (type === 'reassignments') {
                                            cardThemeClass = 'theme-reassignment';
                                            iconEl = <RefreshCw size={14} />;
                                            displayTitle = `${selectedContributor.name} reassigned a section`;
                                            const prevAssignee = act.metadata?.previous_assignee || act.metadata?.from_user || act.metadata?.from || 'Unassigned';
                                            const newAssignee = act.metadata?.new_assignee || act.metadata?.to_user || act.metadata?.to || act.metadata?.assignee_name || act.metadata?.user_names || 'N/A';
                                            const targetDateVal = act.metadata?.deadline || act.metadata?.target_date;

                                            cardContent = (
                                                <div className="activity-card-details">
                                                    <div className="detail-item-row">
                                                        <div className="detail-item">
                                                            <span className="detail-label">From:</span>
                                                            <span className="detail-value">{prevAssignee}</span>
                                                        </div>
                                                        <ArrowRight size={14} className="arrow-transition" />
                                                        <div className="detail-item">
                                                            <span className="detail-label">To:</span>
                                                            <span className="detail-value">{newAssignee}</span>
                                                        </div>
                                                    </div>
                                                    {targetDateVal && (
                                                        <div className="detail-item" style={{ marginTop: '8px' }}>
                                                            <span className="detail-label">Target Date:</span>
                                                            <span className="detail-value date-value">{formatTimelineDate(targetDateVal)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        } else if (type === 'deadline_updates') {
                                            cardThemeClass = 'theme-deadline';
                                            iconEl = <Calendar size={14} />;
                                            displayTitle = `${selectedContributor.name} updated the target date`;
                                            const prevDeadline = act.metadata?.previous_deadline || act.metadata?.old_deadline || act.metadata?.from;
                                            const newDeadline = act.metadata?.new_deadline || act.metadata?.deadline || act.metadata?.to;

                                            cardContent = (
                                                <div className="activity-card-details">
                                                    <div className="detail-item-row">
                                                        {prevDeadline && (
                                                            <>
                                                                <div className="detail-item">
                                                                    <span className="detail-label">From:</span>
                                                                    <span className="detail-value date-value">{formatTimelineDate(prevDeadline)}</span>
                                                                </div>
                                                                <ArrowRight size={14} className="arrow-transition" />
                                                            </>
                                                        )}
                                                        <div className="detail-item">
                                                            <span className="detail-label">To:</span>
                                                            <span className="detail-value date-value highlight-value">{formatTimelineDate(newDeadline)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        } else if (type === 'submissions') {
                                            cardThemeClass = 'theme-submission';
                                            iconEl = <Send size={14} />;
                                            displayTitle = `${selectedContributor.name} marked section as submitted`;

                                            cardContent = (
                                                <div className="activity-card-details">
                                                    <span className="status-badge-pill submitted">Submitted</span>
                                                </div>
                                            );
                                        } else if (type === 'approvals') {
                                            cardThemeClass = 'theme-approval';
                                            iconEl = <Check size={14} />;
                                            displayTitle = `Team Lead approved section`;
                                            const approvedBy = act.metadata?.approved_by || act.actor || 'Team Lead';
                                            const approvedOn = act.timestamp || act.timestamp_utc;

                                            cardContent = (
                                                <div className="activity-card-details">
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <span className="status-badge-pill approved">Approved</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <span className="detail-label">Approved By:</span>
                                                        <span className="detail-value">{approvedBy}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <span className="detail-label">Approved On:</span>
                                                        <span className="detail-value date-value">{formatTimelineDate(approvedOn)}</span>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            cardThemeClass = 'theme-default';
                                            cardContent = (
                                                <div className="activity-card-details">
                                                    {act.metadata && Object.keys(act.metadata).length > 0 && (
                                                        <div className="event-metadata-tags">
                                                            {Object.entries(act.metadata)
                                                                .filter(([k, v]) => !['section_id', 'section_title', 'section_key', 'assignee_id', 'notes', 'note', 'removed_user_ids', 'removed_user_id', 'user_ids', 'user_id', 'remove_all'].includes(k) && v)
                                                                .map(([k, v]) => (
                                                                    <span key={k} className="meta-tag-pill">
                                                                        <span className="meta-tag-key">{k.split('_').join(' ').toUpperCase()}:</span>
                                                                        <span className="meta-tag-val">{formatMetadataValue(v)}</span>
                                                                    </span>
                                                                ))
                                                            }
                                                        </div>
                                                    )}
                                                    {(act.metadata?.notes || act.metadata?.note) && (
                                                        <p className="summary-note-text" style={{ marginTop: '8px', fontStyle: 'italic', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                            "{act.metadata.notes || act.metadata.note}"
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        }

                                        const isActive = activeTimelineCard ? (activeTimelineCard.id === act.id || activeTimelineCard.log_id === act.log_id) : (i === 0);

                                        return (
                                            <div
                                                key={act.id || i}
                                                className={`timeline-row-item ${isActive ? 'active' : ''}`}
                                                onClick={() => setActiveTimelineCard(act)}
                                            >
                                                <div className="timeline-time-col">
                                                    <span className="date-text">{localDate}</span>
                                                    <span className="time-text">{localTime}</span>
                                                </div>

                                                <div className={`timeline-icon-col ${cardThemeClass}`}>
                                                    <div className="timeline-icon-wrapper">
                                                        {iconEl}
                                                    </div>
                                                </div>

                                                <div className="timeline-card-col">
                                                    <div className="activity-timeline-card">
                                                        <span className="activity-card-title">{displayTitle}</span>
                                                        <div className="activity-section-badge">
                                                            <span className="section-label">Section:</span>
                                                            <span className="section-name">{sectionName}</span>
                                                        </div>
                                                        {cardContent}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="timeline-empty-state">
                                        <CalendarDays size={32} className="empty-icon" />
                                        <p>No matching activities found for the selected filters.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        {/* <div className="timeline-modal-footer">
                            <button className="footer-close-btn" onClick={() => setShowContributorModal(false)}>
                                Close
                            </button>
                            <button
                                className="footer-view-section-btn"
                                onClick={handleViewSection}
                                disabled={filteredActions.length === 0}
                            >
                                View Section →
                            </button>
                        </div> */}
                    </div>
                </div>
            )}

            {/* Toast notification */}
            {toast && (
                <div className={`audit-toast-notification ${toast.type}`}>
                    <span>{toast.message}</span>
                </div>
            )}
        </div>
    );
};

export default AuditLogs;
